import { IJobTaskRunDetail } from "@/api/admin/admin.types";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import { CheckCircle2, ChevronDown, Circle, CircleAlert } from "lucide-react";
import {
    DerivedPhaseStatus,
    derivePhaseStatus,
    getPhaseDuration,
    getPhaseStartTime,
    groupTasksByType,
    isPhaseInProgress,
    PhaseDefinition,
} from "../utils";
import { LiveDuration } from "./LiveDuration";
import { TaskTypeGroup } from "./TaskTypeGroup";

const STATUS_CONFIG: Record<
    DerivedPhaseStatus,
    {
        icon: React.ElementType;
        color: string;
        label: string;
        chipBg: string;
        chipText: string;
    }
> = {
    completed: {
        icon: CheckCircle2,
        color: "#16a34a",
        label: "Completed",
        chipBg: "#dcfce7",
        chipText: "#16a34a",
    },
    failed: {
        icon: CircleAlert,
        color: "#dc2626",
        label: "Failed",
        chipBg: "#dc262624",
        chipText: "#dc2626",
    },
    processing: {
        icon: Circle,
        color: "#d97706",
        label: "Processing",
        chipBg: "#d9770624",
        chipText: "#d97706",
    },
    queued: {
        icon: Circle,
        color: "#9ca3af",
        label: "Queued",
        chipBg: "#9ca3af24",
        chipText: "#64748b",
    },
    idle: {
        icon: Circle,
        color: "#d1d5db",
        label: "Idle",
        chipBg: "#e5e7eb24",
        chipText: "#94a3b8",
    },
};

interface PhasePanelProps {
    phase: PhaseDefinition;
    phaseIndex: number;
    tasks: IJobTaskRunDetail[];
    jobFailed: boolean;
    expanded: boolean;
    onToggle: () => void;
}

export const PhasePanel = ({
    phase,
    phaseIndex,
    tasks,
    jobFailed,
    expanded,
    onToggle,
}: PhasePanelProps) => {
    const status = derivePhaseStatus(tasks);
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;
    const duration = getPhaseDuration(tasks);
    const liveStart = getPhaseStartTime(tasks);
    const isLive = isPhaseInProgress(tasks);

    if (tasks.length === 0) {
        return (
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: "#e2e8f0",
                    borderRadius: "10px",
                    px: 2.5,
                    py: 1.75,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Circle size={20} color="#d1d5db" />
                    <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                        Phase {phaseIndex + 1}: {phase.label}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8125rem" }}
                    >
                        {jobFailed ? "Skipped" : "Waiting"}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    return (
        <Accordion
            expanded={expanded}
            onChange={onToggle}
            sx={{
                border: "1px solid",
                borderColor: "#e2e8f0",
                borderRadius: "10px !important",
                "&:before": { display: "none" },
                boxShadow: "none",
                overflow: "hidden",
                transition: "border-color 0.15s ease",
                "&:hover": { borderColor: "#cbd5e1" },
            }}
        >
            <AccordionSummary
                expandIcon={<ChevronDown size={18} />}
                sx={{ px: 2.5, minHeight: 52 }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ width: "100%" }}
                >
                    {status === "processing" ? (
                        <CircularProgress
                            size={20}
                            sx={{ color: config.color }}
                        />
                    ) : (
                        <Icon size={20} color={config.color} />
                    )}

                    <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: "#1e293b" }}
                    >
                        Phase {phaseIndex + 1}: {phase.label}
                    </Typography>

                    <Box sx={{ flex: 1 }} />

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.8125rem" }}
                    >
                        {status === "completed"
                            ? `${tasks.length} task${tasks.length !== 1 ? "s" : ""}`
                            : `${tasks.filter((t) => t.status === "completed").length}/${tasks.length} completed`}
                    </Typography>

                    <Chip
                        label={config.label}
                        size="small"
                        sx={{
                            height: 24,
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            backgroundColor: config.chipBg,
                            color: config.chipText,
                        }}
                    />

                    {isLive && liveStart ? (
                        <LiveDuration
                            startTime={liveStart}
                            variant="body2"
                            sx={{
                                fontVariantNumeric: "tabular-nums",
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                backgroundColor: "#f1f5f9",
                                borderRadius: "6px",
                                px: 1,
                                py: 0.25,
                            }}
                        />
                    ) : duration ? (
                        <Typography
                            variant="body2"
                            sx={{
                                fontVariantNumeric: "tabular-nums",
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                backgroundColor: "#f1f5f9",
                                borderRadius: "6px",
                                px: 1,
                                py: 0.25,
                            }}
                        >
                            {duration}
                        </Typography>
                    ) : null}
                </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 0 }}>
                <Box sx={{ borderTop: "1px solid", borderColor: "#e2e8f0" }}>
                    {Array.from(groupTasksByType(tasks)).map(
                        ([taskType, typeTasks]) => (
                            <TaskTypeGroup
                                key={taskType}
                                taskType={taskType}
                                tasks={typeTasks}
                            />
                        )
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};
